import { useSyncExternalStore } from 'react';

import type { AiTransformationMatrixGrouping } from '../../data-contract/ai-transformation-strategy';

/**
 * What a priority control stands for: a position, a task, a role or a level of one Position Architecture. A role, level
 * or task can show up in several architectures, and each saves its priority on its own.
 */
export type PriorityTarget = {
    readonly architectureId: string;
    readonly id: string;
    readonly kind: AiTransformationMatrixGrouping;
};

export type PriorityOverrides = ReadonlyMap<string, boolean>;

const keyOf = ({ architectureId, id, kind }: PriorityTarget): string => `${architectureId}:${kind}:${id}`;

let overrides: PriorityOverrides = new Map();
const listeners = new Set<() => void>();

const publish = (next: PriorityOverrides): void => {
    overrides = next;
    for (const listener of listeners) listener();
};

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

const snapshot = (): PriorityOverrides => overrides;

/**
 * Whether the target is shown prioritized while its change is saved, on every control that stands for it: the list's
 * checkbox, the matrix's check and the tile's button. Undefined when no change of it is under way.
 */
export const priorityOverrideOf = (current: PriorityOverrides, target: PriorityTarget): boolean | undefined =>
    current.get(keyOf(target));

export const isPriorityChanging = (target: PriorityTarget): boolean => overrides.has(keyOf(target));

export const setPriorityOverride = (target: PriorityTarget, prioritized: boolean): void =>
    publish(new Map(overrides).set(keyOf(target), prioritized));

export const clearPriorityOverride = (target: PriorityTarget): void => {
    if (!overrides.has(keyOf(target))) return;
    const next = new Map(overrides);
    next.delete(keyOf(target));
    publish(next);
};

export const usePriorityOverrides = (): PriorityOverrides => useSyncExternalStore(subscribe, snapshot, snapshot);

/** The prioritized state to show for the target: the one being saved, else the server's. Only its own change renders. */
export const usePrioritizedState = (target: PriorityTarget, serverState: boolean): boolean => {
    const key = keyOf(target);
    const read = () => overrides.get(key);

    return useSyncExternalStore(subscribe, read, read) ?? serverState;
};
